import { SwapMode } from '../../common/types/index.js';
import { BASE_URL, DEFAULT_FEE, DEFAULT_INTEGRATORS_CONFIG, DEFAULT_OPTIONS, DEFAULT_SLIPPAGE, MAX_AMOUNT_USD, MIN_ETH_AMOUNT_USD, MIN_EVM_AMOUNT_USD, NETWORK_TO_CHAIN_ID, SVC, X_CHAIN_ETH_MIN_AMOUNT_USD, } from './constants.js';
import { areAmountsValid, deconstructPairId, getGeolocationKey, memoizeWithTimeout, } from '../../utils.js';
import decodeTransaction from './utils/decode-transaction.js';
import { ETH_NULL_ADDRESS, LOGS, ZERO_ALLOWANCE_ASSETS } from '../../constants.js';
import { APPROVAL_GAS_LIMIT, EXACT_OUT_ALLOWANCE_THRESHOLD, QUOTE_CACHE_TTL } from '../constants.js';
import { isAssetPairExchangeable, parseFetchivalError, serializeQuoteKey } from '../utils.js';
import { CreateDexOrderError, CreateTxError, ExchangeError, InvalidAmountError, InvalidFromTokenAddressError, InvalidToTokenAddressError, LifiProviderError, MissingPairError, MissingPairPriceError, NoRateAvailableError, SendTxError, } from '../../errors.js';
import { simulateEvmTx } from '../simulation.js';
class LiFi {
    name = 'li-fi';
    source = 'lifi';
    _baseUrl;
    _assetsService;
    _gasPriceService;
    _ordersService;
    _pricingService;
    _fetchival;
    _fee;
    _apiKeys;
    _integrators;
    _options;
    _tracer;
    _apiKey;
    _integrator;
    _cachedQuotes = new Map();
    constructor({ apiKeys, assetsService, baseUrl = BASE_URL, options = Object.create(null), ordersService, pricingService, fetchival, gasPriceService, integrators = DEFAULT_INTEGRATORS_CONFIG, feePercentage = DEFAULT_FEE, tracer, }) {
        this._baseUrl = new URL(baseUrl);
        this._assetsService = assetsService;
        this._options = { ...DEFAULT_OPTIONS, ...options };
        this._ordersService = ordersService;
        this._pricingService = pricingService;
        this._fee = feePercentage;
        this._gasPriceService = gasPriceService;
        this._apiKeys = apiKeys;
        this._integrators = integrators;
        this._tracer = tracer;
        this._fetchival = fetchival;
        this._integrator = this._integrators.default;
        this._apiKey = this._apiKeys?.default;
    }
    async isPairSupported(pairId) {
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset, assetSources] = await Promise.all([
            this._assetsService.getOneById(from),
            this._assetsService.getOneById(to),
            this._assetsService.getAssetSources([this.source], [from, to]),
        ]);
        if (fromAsset?.network !== toAsset?.network) {
            return false;
        }
        return isAssetPairExchangeable({ from, to, assetSources });
    }
    async getQuote(params) {
        const { pairId, amount, fromAddress, slippage, swapMode = SwapMode.ExactIn } = params;
        const quoteCacheKey = serializeQuoteKey({ pairId, amount, fromAddress, slippage, swapMode });
        const cachedQuote = this._cachedQuotes.get(quoteCacheKey);
        if (!cachedQuote || cachedQuote.expiry < Date.now()) {
            const quote = await this._getQuote(params);
            this._cachedQuotes.set(quoteCacheKey, { quote, expiry: Date.now() + QUOTE_CACHE_TTL });
        }
        return this._cachedQuotes.get(quoteCacheKey).quote;
    }
    async _getQuote(params) {
        const { pairId, amount, fromAddress, toAddress, slippage = DEFAULT_SLIPPAGE, swapMode = SwapMode.ExactIn, getAllowance, } = params;
        const { from, to } = deconstructPairId(pairId);
        const [{ fromAsset, toAsset }, assetSources] = await Promise.all([
            this._getAssetsFromPairId(pairId),
            this._assetsService.getAssetSources([this.source], [from, to]),
        ]);
        if (!isAssetPairExchangeable({ from, to, assetSources })) {
            throw new Error(`Pair ${pairId} is not exchangeable`);
        }
        const [quote, limits] = await Promise.all([
            this._getQuoteFromLifi({
                amount,
                fromAddress,
                fromAsset,
                slippage,
                toAsset,
                toAddress,
                swapMode,
            }),
            this.getLimits({ pairId }),
        ]);
        const limit = swapMode === SwapMode.ExactIn ? limits[0]?.ExactIn : limits[0]?.ExactOut;
        if (!limit || amount.lt(limit.min) || amount.gt(limit.max)) {
            const { value: minValue = 'UNDEF_VAL' } = limit?.min || {};
            const { value: maxValue = 'UNDEF_VAL' } = limit?.max || {};
            throw new NoRateAvailableError({
                max: maxValue.toString(),
                min: minValue.toString(),
                amount: amount.toDefaultString(),
            });
        }
        const allowance = await getAllowance({
            approvalAddress: quote.estimate.approvalAddress,
            assetId: fromAsset.id,
        });
        const fromAmount = swapMode === SwapMode.ExactIn
            ? amount
            : fromAsset.currency.baseUnit(quote.estimate.fromAmount);
        const fromTokenAddress = this._getTokenAddress(fromAsset);
        const shouldApproveTokens = fromTokenAddress !== ETH_NULL_ADDRESS && allowance.lt(fromAmount);
        const shouldResetApprovalTokens = allowance.isPositive && ZERO_ALLOWANCE_ASSETS.includes(fromAsset.id);
        const approvalGasLimit = shouldApproveTokens
            ? shouldResetApprovalTokens
                ? APPROVAL_GAS_LIMIT * 2
                : APPROVAL_GAS_LIMIT
            : 0;
        const txGasLimit = this._estimateGasLimit(quote.estimate.gasCosts);
        const quoteAmount = swapMode === SwapMode.ExactIn
            ? toAsset.currency.baseUnit(quote.estimate.toAmount)
            : fromAsset.currency.baseUnit(quote.estimate.fromAmount);
        const rate = swapMode === SwapMode.ExactIn
            ? quoteAmount.div(amount.toDefaultString()).toDefaultString()
            : amount.div(quoteAmount.toDefaultString()).toDefaultString();
        return {
            providerName: this.name,
            rateId: quote.id,
            amount: quoteAmount,
            rate,
            exchangeFeeData: {
                feeOpts: {
                    gasLimit: txGasLimit + approvalGasLimit,
                },
            },
            exchangeTxData: {
                transactionRequest: quote.transactionRequest,
                approvalAddress: quote.estimate.approvalAddress,
            },
        };
    }
    async prepareExchange(params) {
        try {
            const { fromAddress, fromAmount, pairId, slippage = DEFAULT_SLIPPAGE, toAddress, toAmount, isSendAll, anonymousId, referralCode, swapMode = SwapMode.ExactIn, quote, createTx, onOrderIdGenerated, simulateTx, } = params;
            const { fromAsset } = await this._getAssetsFromPairId(pairId);
            this._traceMessage(LOGS.CREATING_ORDER);
            this._tracer?.traceTime('LogDexOrder');
            const orderPayload = {
                fromAmount: fromAmount.toDefaultString({ unit: false }),
                fromAddress,
                toAddress,
                toAmount: toAmount.toDefaultString({ unit: false }),
                pairId,
                slippage,
                svc: SVC,
                referralCode,
                swapMode,
            };
            const order = await this._ordersService
                .insertDex(orderPayload, { anonymousId })
                .catch(async (err) => {
                this._traceMessage(LOGS.FAILED_TO_CREATE_ORDER);
                const errorMessage = await parseFetchivalError(err);
                throw new CreateDexOrderError({
                    message: errorMessage,
                    details: orderPayload,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('LogDexOrder'));
            if (typeof onOrderIdGenerated === 'function') {
                onOrderIdGenerated(order.id);
            }
            const { transactionRequest, approvalAddress } = quote.exchangeTxData;
            this._traceMessage(LOGS.DECODING_TX);
            const lifiTransaction = decodeTransaction(transactionRequest.data, fromAddress);
            this._traceMessage(LOGS.CHECKING_ORDER_VALIDITY);
            this._tracer?.traceTime('CheckOrderIsValid');
            const orderCheckPromise = this._checkOrderIsValid({
                lifiTransaction,
                order: { ...order, slippage, swapMode },
            })
                .catch((err) => {
                this._traceMessage(LOGS.FAILED_TO_VALIDATE_ORDER);
                throw err;
            })
                .finally(() => this._tracer?.traceTimeEnd('CheckOrderIsValid'));
            this._traceMessage(LOGS.CREATING_TX);
            this._tracer?.traceTime('CreateTx');
            const createTxPayload = {
                assetId: fromAsset.id,
                txData: {
                    chainId: transactionRequest.chainId,
                    data: transactionRequest.data,
                    from: transactionRequest.from,
                    gasLimit: quote.exchangeFeeData.feeOpts.gasLimit,
                    gasPrice: quote.exchangeFeeData.feeOpts?.gasPrice,
                    tipGasPrice: quote.exchangeFeeData.feeOpts?.tipGasPrice,
                    to: transactionRequest.to,
                    value: transactionRequest.value,
                },
                isSendAll,
            };
            const simulateTxPromise = simulateEvmTx({
                tracer: this._tracer,
                simulate: simulateTx,
                data: {
                    baseAssetName: fromAsset.network,
                    swapTx: {
                        chainId: transactionRequest.chainId.toString(),
                        from: transactionRequest.from,
                        to: transactionRequest.to,
                        gasPrice: `0x${quote.exchangeFeeData.feeOpts?.gasPrice?.toBaseNumber().toString(16)}`,
                        gas: transactionRequest.gasLimit,
                        value: transactionRequest.value,
                        data: transactionRequest.data,
                        nonce: undefined,
                        maxPriorityFeePerGas: undefined,
                        maxFeePerGas: undefined,
                    },
                    origin: this.name,
                    senderAddress: fromAddress,
                    approveToken: {
                        approveAmount: fromAmount,
                        assetId: fromAsset.id,
                        chainId: transactionRequest.chainId.toString(),
                        spenderAddress: approvalAddress,
                    },
                },
            });
            const transactionPromise = createTx(createTxPayload)
                .catch((err) => {
                this._traceMessage(LOGS.FAILED_TO_CREATE_TX);
                throw new CreateTxError({
                    message: err.message,
                    details: createTxPayload,
                    orderId: order.id,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('CreateTx'));
            const [transaction] = await Promise.all([transactionPromise, simulateTxPromise, orderCheckPromise]);
            return {
                transaction,
                orderId: order.id,
                fromAmount: swapMode === SwapMode.ExactIn
                    ? fromAmount
                    : fromAmount.mul(1 + slippage / 100 + EXACT_OUT_ALLOWANCE_THRESHOLD),
                approvalAddress,
            };
        }
        catch (err) {
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message }, { cause: err });
        }
    }
    async executeExchange({ pairId, fromAmount, transaction, orderId, approvalAddress, approveTokens, sendTx, }) {
        try {
            const { fromAsset } = await this._getAssetsFromPairId(pairId);
            this._traceMessage(LOGS.CHECKING_ALLOWANCE);
            this._tracer?.traceTime('GetAllowance');
            const transactions = [];
            if (approvalAddress) {
                const approvalTxs = await approveTokens({
                    assetId: fromAsset.id,
                    approveAmount: fromAmount,
                    spenderAddress: approvalAddress,
                });
                transactions.push(...approvalTxs);
            }
            this._traceMessage(LOGS.SENDING_TX);
            this._tracer?.traceTime('SendTx');
            const { txId } = await sendTx(transaction)
                .catch((err) => {
                this._traceMessage(LOGS.FAILED_TO_SEND_TX);
                throw new SendTxError({
                    message: err.message,
                    details: transaction,
                });
            })
                .finally(() => this._tracer?.traceTimeEnd('SendTx'));
            transactions.push({ type: 'swapTransaction', id: txId });
            this._traceMessage(LOGS.UPDATING_ORDER);
            this._ordersService
                .updateDex({
                id: orderId,
                transactionId: txId,
            })
                .catch((err) => console.warn(`Fail to update order ${orderId} with txId ${txId}`, err));
            return { transactions, providerOrderId: txId, svc: SVC };
        }
        catch (err) {
            if (err instanceof ExchangeError) {
                throw err;
            }
            throw new ExchangeError({ message: err.message }, { cause: err });
        }
    }
    async getLimits({ pairId }) {
        const { from, to } = deconstructPairId(pairId);
        const [assetSources, { fromAsset, toAsset }, usdPrices] = await Promise.all([
            this._assetsService.getAssetSources([this.source], [from, to]),
            this._getAssetsFromPairId(pairId),
            this._pricingService.getPrices([from, to]),
        ]);
        if (!isAssetPairExchangeable({ from, to, assetSources })) {
            throw new Error(`Pair ${pairId} is not exchangeable`);
        }
        const minUsdLimitBasedOnGasPrice = await this._gasPriceService
            .getMinUsdLimitBasedOnGasPrice(fromAsset.id)
            .catch(() => 0);
        const { min: minimumBasedOnUsdEquivalent, max } = this._getUsdLimitAmountsForSwap({
            fromAsset,
            toAsset,
        });
        const fromPrice = usdPrices[fromAsset.id];
        const toPrice = usdPrices[toAsset.id];
        if (!fromPrice || !toPrice) {
            throw new MissingPairPriceError({
                pairId,
                fromAssetPrice: fromPrice,
                toAssetPrice: toPrice,
            });
        }
        const exactInMinAmount = Math.max(minimumBasedOnUsdEquivalent, minUsdLimitBasedOnGasPrice || 0) / fromPrice;
        const exactInMaxAmount = max / fromPrice;
        const exactOutMinUsdAmount = Math.max(minimumBasedOnUsdEquivalent, minUsdLimitBasedOnGasPrice || 0) / toPrice;
        const exactOutMaxAmount = max / toPrice;
        return [
            {
                [SwapMode.ExactIn]: {
                    min: fromAsset.currency.defaultUnit(exactInMinAmount),
                    max: fromAsset.currency.defaultUnit(exactInMaxAmount),
                },
                [SwapMode.ExactOut]: {
                    min: toAsset.currency.defaultUnit(exactOutMinUsdAmount),
                    max: toAsset.currency.defaultUnit(exactOutMaxAmount),
                },
            },
        ];
    }
    setGeolocation(geolocation) {
        if (!geolocation) {
            this._apiKey = this._apiKeys?.default;
            this._integrator = this._integrators.default;
            return;
        }
        const geolocationKey = getGeolocationKey(geolocation);
        if (this._apiKeys?.hasOwnProperty(geolocationKey)) {
            this._apiKey = this._apiKeys[geolocationKey];
        }
        else {
            this._apiKey = this._apiKeys?.default;
        }
        if (this._integrators.hasOwnProperty(geolocationKey)) {
            this._integrator = this._integrators[geolocationKey];
        }
        else {
            this._integrator = this._integrators.default;
        }
    }
    _getUsdLimitAmountsForSwap = (params) => {
        const { fromAsset, toAsset } = params;
        const isXChain = fromAsset.network !== toAsset.network;
        if (isXChain) {
            const isFromEthereum = fromAsset.network === 'ethereum';
            return { min: isFromEthereum ? X_CHAIN_ETH_MIN_AMOUNT_USD : 50, max: MAX_AMOUNT_USD };
        }
        const minAmountUSD = fromAsset.network === 'ethereum' ? MIN_ETH_AMOUNT_USD : MIN_EVM_AMOUNT_USD;
        return { min: minAmountUSD, max: MAX_AMOUNT_USD };
    };
    _getQuoteFromLifi = async ({ amount, fromAddress, fromAsset, slippage, toAddress, toAsset, swapMode = SwapMode.ExactIn, }) => {
        const baseAmount = amount.toBaseString({ unit: false });
        const fromChain = `${NETWORK_TO_CHAIN_ID.get(fromAsset.network)}`;
        const toChain = `${NETWORK_TO_CHAIN_ID.get(toAsset.network)}`;
        const quoteParams = {
            fromAddress,
            fromChain,
            fromToken: this._getTokenAddress(fromAsset),
            integrator: this._integrator,
            fee: this._fee,
            toChain,
            toToken: this._getTokenAddress(toAsset),
            ...(swapMode === SwapMode.ExactIn ? { fromAmount: baseAmount } : { toAmount: baseAmount }),
            ...(toAddress ? { toAddress } : {}),
            ...(slippage ? { slippage: slippage / 100 } : {}),
            ...(this._options.includePartners?.length
                ? { allowBridges: this._options.includePartners }
                : {}),
        };
        const response = (await this._request(swapMode === SwapMode.ExactIn ? 'quote' : 'quote/toAmount').get(quoteParams));
        if (swapMode === SwapMode.ExactOut) {
            const estimatedFromAmountWithSlippage = fromAsset.currency.baseUnit(response.estimate.fromAmount);
            const slippagePercentage = slippage / 100;
            const slippageEquivalent = estimatedFromAmountWithSlippage.mul(slippagePercentage);
            const fromAmountWithoutSlippage = estimatedFromAmountWithSlippage.sub(slippageEquivalent);
            response.estimate.fromAmount = fromAmountWithoutSlippage.toBaseString();
        }
        return response;
    };
    _estimateGasLimit(lifiGasCosts) {
        return lifiGasCosts.reduce((acc, gasCost) => {
            const gasPrice = parseInt(gasCost.price);
            const estimate = parseInt(gasCost.amount);
            if (gasPrice > 0 && estimate > 0) {
                const estimatedLowGasLimit = estimate / gasPrice;
                const estimatedHighGasLimit = parseInt(gasCost.limit);
                const difference = estimatedHighGasLimit - estimatedLowGasLimit;
                const estimatedGasLimit = difference > 0
                    ? estimatedLowGasLimit + difference * (this._options.gasLimitMultiplier || 1)
                    : estimatedLowGasLimit;
                return Math.ceil(acc + estimatedGasLimit);
            }
            return acc;
        }, 0);
    }
    _getAssetsFromPairId = memoizeWithTimeout(async (pairId) => {
        const { from, to } = deconstructPairId(pairId);
        const [fromAsset, toAsset] = await Promise.all([
            this._assetsService.getOneById(from),
            this._assetsService.getOneById(to),
        ]);
        if (!fromAsset || !toAsset) {
            throw new MissingPairError({
                pairId,
                fromAsset,
                toAsset,
            });
        }
        return {
            fromAsset,
            toAsset,
        };
    });
    async _checkOrderIsValid({ lifiTransaction, order, }) {
        const { from, to } = deconstructPairId(order.pairId);
        const fromAsset = await this._assetsService.getOneById(from);
        const toAsset = await this._assetsService.getOneById(to);
        if (!fromAsset || !toAsset) {
            throw new MissingPairError({
                pairId: order.pairId,
                fromAsset,
                toAsset,
            });
        }
        const fromTokenAddress = this._getTokenAddress(fromAsset);
        const toTokenAddress = this._getTokenAddress(toAsset);
        const depositSwapData = lifiTransaction.swapData[0];
        const receivingSwapData = lifiTransaction.swapData[lifiTransaction.swapData.length - 1];
        if (!depositSwapData || !receivingSwapData) {
            throw new LifiProviderError({ message: 'Invalid Order: no swap data' });
        }
        const fromAmount = fromAsset.currency.baseUnit(depositSwapData.fromAmount);
        const fromAmountWithoutSlippage = order.swapMode === SwapMode.ExactOut ? fromAmount.mul(1 - order.slippage / 100) : fromAmount;
        const amountsValid = areAmountsValid(fromAmountWithoutSlippage.toDefaultString(), String(order.amount.value));
        if (!amountsValid) {
            throw new InvalidAmountError({
                order,
                providerOrder: lifiTransaction,
                actual: fromAmountWithoutSlippage.toDefaultString(),
                expected: order.amount.value.toString(),
            });
        }
        if (lifiTransaction.receiver.toLowerCase() !== order.toAddress.toLowerCase()) {
            throw new InvalidToTokenAddressError({
                actual: lifiTransaction.receiver,
                expected: order.toAddress,
                order,
                providerOrder: lifiTransaction,
            });
        }
        if (depositSwapData.sendingAssetId.toLowerCase() !== fromTokenAddress) {
            throw new InvalidFromTokenAddressError({
                actual: depositSwapData.sendingAssetId.toLowerCase(),
                expected: fromTokenAddress,
                order,
                providerOrder: lifiTransaction,
            });
        }
        if (fromAsset.network === toAsset.network &&
            receivingSwapData.receivingAssetId.toLowerCase() !== toTokenAddress) {
            throw new InvalidToTokenAddressError({
                actual: receivingSwapData.receivingAssetId.toLowerCase(),
                expected: toTokenAddress,
                order,
                providerOrder: lifiTransaction,
            });
        }
    }
    _getTokenAddress = (asset) => {
        return asset.meta?.contractAddress?.toLowerCase() || ETH_NULL_ADDRESS;
    };
    _request(subpath) {
        const headers = {
            ...(this._apiKey ? { 'x-lifi-api-key': this._apiKey } : {}),
        };
        const uri = new URL(`${this._baseUrl}/${subpath}`);
        return this._fetchival(uri, { headers });
    }
    _traceMessage(message) {
        this._tracer?.traceMessage(message);
    }
}
export default LiFi;
